import React, { useEffect, useState } from "react";
import BlockPicker from "./BlockPicker";
import useHubBlocks from "../hooks/useHubBlocks";
import { inspectAddress, floorFromFlat } from "../Helper/addressBlocks";

const API_BASE = "https://dailydish.in/api";

/**
 * One-time confirmation of an existing address's block and flat.
 *
 * Addresses saved before the block picker existed have no usable block: 85 of
 * 130 at one apartment hub were stored as addressType "Home" with the flat
 * buried in free text ("Alpine pyramid g block 506", "#605 g-block"), and the
 * PG ones held 29 different spellings of ~7 towers. None of it can be grouped
 * by the rider app.
 *
 * There is no "already shown" flag. Whether to show this is decided by reading
 * the address's own data via inspectAddress(), so the moment a customer saves
 * through the picker the answer flips to false permanently and they are never
 * asked again. Nothing to reset, and no way to double-prompt.
 *
 * The save edits the SAME address _id. A new address would orphan the plans
 * that reference it (MyPlan stores addressId).
 */
const AddressFixModal = ({
  address,
  show = true,
  mandatory = false,
  onSaved,
  onDismiss,
}) => {
  const { blocks, loading } = useHubBlocks(address?.hubId);
  const [block, setBlock] = useState("");
  const [flat, setFlat] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const verdict = inspectAddress(address, blocks);

  // Pre-fill from whatever the customer already wrote. For most people this
  // makes the whole interaction a single tap on Save.
  useEffect(() => {
    if (loading || !address) return;
    setBlock((current) => current || verdict.suggestedBlock || "");
    setFlat((current) => current || verdict.suggestedFlat || "");
    // verdict is derived from address+blocks; those are the real inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, address?._id, blocks]);

  // Every reason this modal can stay hidden is silent by design — an address
  // that cannot be prompted for looks identical to one that does not need it.
  // That makes "why is there no popup?" impossible to answer from the outside,
  // so say it out loud instead.
  const hide = (reason, detail) => {
    console.debug("[AddressFixModal] not shown:", reason, {
      addressId: address?._id,
      addressType: address?.addressType,
      hubId: address?.hubId,
      hubName: address?.hubName,
      towerBlock: address?.towerBlock,
      flat: address?.flat,
      blocksLoaded: blocks,
      ...detail,
    });
    return null;
  };

  if (!show || !address) return hide("no address in state");

  // Wait for the block list before deciding. Without it every address looks
  // broken, and we would flash this modal at people who are already fine.
  if (loading) return null;

  if (!address.hubId) {
    return hide(
      "address has no hubId, so its hub's block list cannot be looked up",
    );
  }

  if (blocks.length === 0) {
    return hide(
      "hub has no blocks configured (or the /blocks endpoint is not deployed)",
    );
  }

  if (!verdict.needsFix) {
    return hide("address already has a valid block and flat", { verdict });
  }

  const canSave = Boolean(block) && floorFromFlat(flat) !== null;

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    setError("");

    try {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      const customerId = user?._id;
      if (!customerId) throw new Error("Please log in again");

      const apartmentName =
        address.apartmentName || address.hubName || address.houseName || "";

      const response = await fetch(
        `${API_BASE}/User/customers/${customerId}/addresses/${address._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            // Home addresses at an apartment hub become Apartment addresses.
            addressType: "Apartment",
            apartmentName,
            towerBlock: block,
            flat,
            // floor is derived server-side from flat; sent for older clients.
            floor: String(floorFromFlat(flat)),
          }),
        },
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Could not save. Please try again.");
      }

      // Prefer the saved document the API echoes back. The server derives the
      // floor and rebuilds `fullAddress` — the string every screen actually
      // displays — so reconstructing it here would drift from what was stored,
      // and the header would keep showing the old free-text address.
      const updated = data.address || {
        ...address,
        addressType: "Apartment",
        apartmentName,
        towerBlock: block,
        flat,
        floor: String(floorFromFlat(flat)),
      };

      // Keep the cached copy in step, otherwise the next screen re-reads the
      // stale address from localStorage and re-opens this modal.
      const stored = localStorage.getItem("primaryAddress");
      if (stored && stored !== "null") {
        const parsed = JSON.parse(stored);
        if (parsed?._id === address._id) {
          localStorage.setItem("primaryAddress", JSON.stringify(updated));
        }
      }

      // Other screens (Home header, checkout) read the address from
      // localStorage on these events rather than from this component's state.
      window.dispatchEvent(new Event("addressUpdated"));

      onSaved?.(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 20000,
        padding: 16,
      }}
      onClick={() => {
        if (!mandatory && !saving) onDismiss?.();
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#FFF",
          borderRadius: 16,
          padding: "24px 20px",
          width: "100%",
          maxWidth: 420,
          boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
        }}
      >
        <h5 style={{ margin: 0, fontWeight: 700, fontSize: 18 }}>
          Confirm your flat details
        </h5>
        <p style={{ fontSize: 13, color: "#666", margin: "6px 0 18px" }}>
          {mandatory
            ? "We need your block and flat number to deliver this order."
            : "Help our riders reach your door — it takes a second."}
        </p>

        {(address.apartmentName || address.hubName) && (
          <p style={{ fontSize: 14, fontWeight: 600, margin: "0 0 14px" }}>
            {address.apartmentName || address.hubName}
          </p>
        )}

        <BlockPicker
          blocks={blocks}
          block={block}
          flat={flat}
          onBlockChange={setBlock}
          onFlatChange={setFlat}
          disabled={saving}
        />

        {error && (
          <p style={{ color: "#C0392B", fontSize: 13, marginTop: 12 }}>
            {error}
          </p>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
          {!mandatory && (
            <button
              type="button"
              onClick={onDismiss}
              disabled={saving}
              style={{
                flex: 1,
                height: 46,
                borderRadius: 12,
                border: "1px solid #DDD",
                background: "#FFF",
                color: "#666",
                fontSize: 15,
                cursor: "pointer",
              }}
            >
              Later
            </button>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave || saving}
            style={{
              flex: 2,
              height: 46,
              borderRadius: 12,
              border: "none",
              background: canSave ? "#6B8E23" : "#C8C8C8",
              color: "#FFF",
              fontSize: 15,
              fontWeight: 600,
              cursor: canSave && !saving ? "pointer" : "not-allowed",
            }}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddressFixModal;
